import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Users, 
  Target, 
  Shield,
  TrendingUp,
  Heart,
  Zap,
  Download,
  FileText,
  CheckCircle
} from 'lucide-react';

const About = () => {
  // Fonction de téléchargement direct
  const handleDownloadBrochure = () => {
    //  le chemin brochure PDF
    const brochureUrl = '/Brochure_LST_Oil_SA.pdf';
    
    // Création lien invisible pour le téléchargement
    const link = document.createElement('a');
    link.href = brochureUrl;
    link.download = 'LST-OIL-SA-Brochure.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
  
  };

   const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LST OIL SA",
    "alternateName": "LST OIL International",
    "url": "https://www.lstoilsa.com/about",
    "logo": "https://www.lstoilsa.com/images/logo.png",
    "description": "LST OIL SA - Leader national en hydrocarbures, logistique internationale et produits alimentaires. Découvrez notre histoire, mission et équipe.",
    "foundingDate": "2010",
    "numberOfEmployees": "100+",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SN",
      "addressRegion": "Dakar",
      "addressLocality": "Dakar"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": ["French", "English", "Spanish", "Arabic"]
    },
    "areaServed": ["World", "Africa", "Europe", "Asia", "Middle East"],
    "knowsAbout": [
      "International logistics",
      "Hydrocarbons global market",
      "Food products export",
      "International trade",
      "Supply chain management"
    ]
  };


  const values = [
    {
      icon: Zap,
      title: "Innovation",
      description: "Nous adoptons les nouvelles technologies pour améliorer nos services."
    },
    {
      icon: TrendingUp,
      title: "Excellence",
      description: "Nous visons l'excellence dans tous nos services et opérations."
    },
    {
      icon: Heart,
      title: "Intégrité",
      description: "Nous agissons avec honnêteté, transparence et éthique."
    },
    {
      icon: Shield,
      title: "Sécurité",
      description: "La sécurité de nos employés, clients et biens est notre priorité absolue."
    }
  ];
  // historique LST --> LST OIL SA
  const milestones = [
    { year: "2010", title: "Fondation", description: "Création de LST" },
    { year: "2012", title: "Nos premiers pas vers la logistique", description: "Ouverture des services logistiques" },
    { year: "2015", title: "Internationalisation", description: "Développement des services exports" },
    { year: "2020", title: "Diversification & Innovation Technologique", description: "Entrée dans l'agriculture et Propostion de Services digitaux et conseils" },
    { year: "2025", title: "Ouverture du Capitale", description: "Dévéloppement des procédures" }
  ];
// team LST OIL SA
  const team = [
    {
      name: "Mr Aliou Moussa Sow",
      position: "Président Conseil Administratif",
      description: "Expert en Commandement, Leadership&Management avec 30+ ans d'expérience"
    },
    {
      name: "Mr Lamine Sow",
      position: "Directeur Général",
      description: "Expert en logistique avec 20+ ans d'expérience dans le management, le conseil, la négociation et la gestion des affaires"
    },
    
    {
      name: "Mr Oumar Moussa Sow",
      position: "Directeur Général Adjoint",
      description: "Auditeur-Comptable, Spécialiste Management de la qualité avec 30+ ans d'expérience"
    },
    {
      name: "Mr Ablaye Ndiaye",
      position: "Consultant Oil and GAS",
      description: "Expertise secteur pétrolier, Maîtrise et Gestion opérationnelle du secteur pétrolier avec 30+ ans d'expérience"
    }
  ];

  return (
    <>
     <Helmet>
        <title>À Propos - LST OIL SA | Leader Hydrocarbures & Logistique Internationale</title>
        <meta name="description" content="Découvrez l'histoire, la mission et l'équipe de LST OIL SA, leader mondial en hydrocarbures, logistique internationale et produits alimentaires. 15+ ans d'expertise." />
        <meta name="keywords" content="LST OIL SA, à propos, histoire, mission, valeurs, équipe dirigeante, hydrocarbures international, logistique mondiale, Afrique, Europe, Asie, Moyen-Orient" />

        <meta property="og:title" content="À Propos - LST OIL SA | Leader Hydrocarbures & Logistique Internationale" />
        <meta property="og:description" content="Découvrez l'histoire, la mission et l'équipe de LST OIL SA, leader mondial en hydrocarbures et logistique internationale" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lstoilsa.com/about" />
        <meta property="og:image" content="https://www.lstoilsa.com/images/about-og.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="À Propos - LST OIL SA International" />
        <meta name="twitter:description" content="Leader national en hydrocarbures, logistique internationale et produits alimentaires" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-[#6BB8B1] via-[#4B9D96] to-[#3A7C76] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-8 md:mb-6 font-heading">
              À propos de LST OIL SA
            </h1>
            <p className="text-lg md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
              Plus de 13 ans d'expérience dans les hydrocarbures, 
              la logistique et les services en Afrique et dans le monde
            </p>
            
            {/* CTA Brochure dans le Hero - Téléchargement direct */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 md:mt-12"
            >
              <button
                onClick={handleDownloadBrochure}
                className="bg-[#cd3127] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#a82820]  inline-flex items-center shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform"
              >
                <Download className="w-5 h-5 mr-2" />
                Télécharger notre brochure
              </button>
              <p className="text-white/80 text-sm mt-2">
                PDF 2.4 MB • Accès immédiat et gratuit
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
                Notre Mission
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Fournir des services de logistique et d'hydrocarbures de qualité supérieure, 
                en respectant les plus hauts standards de sécurité et de fiabilité, 
                tout en contribuant au développement durable de nos communautés.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Nous nous engageons à offrir des solutions personnalisées qui répondent 
                aux besoins spécifiques de chaque client, en nous appuyant sur notre 
                expertise technique et notre réseau international.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-yellow-400 to-orange-500 p-6 md:p-8 rounded-2xl text-white"
            >
              <Target className="w-12 h-12 md:w-16 md:h-16 mb-4 md:mb-6 text-white" />
              <h3 className="text-xl md:text-2xl font-bold mb-4">Notre Vision</h3>
              <p className="text-base md:text-lg leading-relaxed">
                Devenir le leader régional des services logistiques et d'hydrocarbures,  
                reconnu pour notre innovation, notre fiabilité et notre contribution 
                au développement économique de l'Afrique.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Nos Valeurs
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Les principes qui guident nos actions et définissent notre culture d'entreprise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-[#6bb8b1] w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <value.icon className="w-8 h-8 text-[#3a7c76]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brochure Download Section - Simplifiée */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#6BB8B1] to-[#4B9D96] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <FileText className="w-16 h-16 mx-auto mb-6 text-white" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading">
                Téléchargez notre brochure complète
              </h2>
              <p className="text-lg md:text-xl mb-8 leading-relaxed">
                Découvrez en détail nos services, notre expertise et nos réussites. 
                Accès immédiat et gratuit.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
                {[
                  "Services détaillés et expertises",
                  "Études de cas clients réussis", 
                  "Notre approche qualité et sécurité"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center justify-center md:justify-start">
                    <CheckCircle className="w-5 h-5 text-[#cd3127] mr-3 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Bouton de téléchargement principal */}
              <div className="space-y-4">
                <button
                  onClick={handleDownloadBrochure}
                  className="bg-[#cd3127] text-white px-10 py-5 rounded-xl font-bold hover:bg-[#a82820] transition-colors inline-flex items-center shadow-2xl hover:shadow-3xl transform hover:scale-105  text-lg"
                >
                  <Download className="w-6 h-6 mr-3" />
                  Télécharger la brochure maintenant
                </button>
                
                <div className="text-white/80 text-sm space-y-1">
                  <p>📥 Téléchargement instantané</p>
                  <p>📄 Format PDF • 2.4 MB</p>
                  <p>🎯 Aucune inscription requise</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brochure CTA Sticky en bas pour mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#cd3127] text-white p-4 shadow-2xl lg:hidden z-40">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-6 h-6 mr-3" />
            <span className="font-semibold">Brochure gratuite</span>
          </div>
          <button
            onClick={handleDownloadBrochure}
            className="bg-white text-[#cd3127] px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </button>
        </div>
      </div>

      {/* History */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Notre Histoire
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Un parcours marqué par la croissance, l'innovation et l'engagement envers nos clients
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-blue-200 h-full hidden lg:block"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex-col lg:gap-8`}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}>
                    <div className="bg-[#4b9d96] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-4 lg:mb-0">
                      <span className="font-bold text-lg">{milestone.year}</span>
                    </div>
                  </div>
                  
                  <div className="lg:w-1/2">
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-heading">
              Notre Équipe
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Des professionnels expérimentés et passionnés qui font la force de LST OIL SA
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="bg-[#6bb8b1] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-[#3a7c76]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-[#4B9D96] font-medium mb-3">
                  {member.position}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {member.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-20 bg-[#4B9D96] text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading">
              LST OIL SA en Chiffres
            </h2>
            <p className="text-lg md:text-xl text-white max-w-3xl mx-auto leading-relaxed">
              Des résultats qui témoignent de notre croissance et de notre engagement
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "13+", label: "Années d'expérience" },
              { number: "500+", label: "Clients satisfaits" },
              { number: "15+", label: "Pays desservis" },
              { number: "100+", label: "Employés directs et indirects" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#cd3127] mb-2">
                  {stat.number}
                </div>
                <div className="text-white text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center mt-12 pt-8 border-t border-white/20"
          >
            <button
              onClick={handleDownloadBrochure}
              className="bg-white text-[#cd3127] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5 mr-2" />
              Télécharger notre brochure
            </button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
